import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import VatMark from '../vatMark'
import {
    listStatements, statementPeriods, runStatements, markStatementPaid, statementPdfUrl,
    unmarkStatementPaid, statementPaymentReturned, sepaRuns, markSepaRunPaid, type SepaRun,
    listAuthors, listAuthorSales, rechargeAuthorSales, statementLines, type StatementLine,
    sepaPreview, sepaExport, confirmStatementIban, type SepaLine, type SepaSkipped,
    type StatementRow, type AuthorRow, type AuthorSaleRow,
} from '../api'
import { BRANDS, formatMoney } from '../lookups'
import { plural, fill, SALE_STATE_LABELS, SALE_KIND_LABELS } from '../../shared/texts'
import { useTabs, useDataVersion } from '../layout/TabsContext'
import { useToast } from '../layout/ToastContext'
import { useAuth } from '../layout/AuthContext'
import InfoTip from '../components/InfoTip'
import ConfirmDialog from '../components/ConfirmDialog'
import { LOOKUP_FETCH_LIMIT, AUTHOR_BRAND } from '../../shared/constants'
import { PAGES_AUTHOR_STATEMENTS_TEXTS as T } from '../../shared/texts'

// The monthly settlement with the authors, in the order the work actually happens:
// run the month → check the drafts → finalize (that is when the document number is assigned)
// → transfer the money → mark as paid.
//
// Nothing here is automatic. A settlement moves money out of the house, so every step that
// cannot be taken back is a deliberate click.

const MONTHS = T.months

function periodLabel(period: string): string {
    const [y, m] = period.split('-').map(Number)
    return `${MONTHS[m - 1]} ${y}`
}

// Author amounts carry no currency of their own — the backoffice one (constants.ts).
const eur = (n: number) => formatMoney(n ?? 0)

// ⚠️ „steht an" und NICHT „offen", obwohl es bei den Verkaeufen genau dasselbe bedeutet.
// In DIESER Liste steht in der Fusszeile „noch zu ueberweisen" — daneben laese sich „offen"
// als ZAHLUNG offen. Dieselbe Verwechslung wie bei „ausgebucht", nur an anderer Stelle
// (Betreiber, 09.09.2026). Dass dasselbe Wort in der Verkaufsliste richtig und hier falsch
// ist, ist kein Widerspruch, sondern der Unterschied zwischen zwei Listen.
const STATUS: Record<StatementRow['status'], string> = T.status

// ⛔ WO DAS GELD STEHT — und das ist eine andere Frage als „wie weit ist das Dokument".
//
// Der Anlass, Betreiber am 10.09.2026: „Ich habe eine SEPA-Datei erzeugt aber bei den Autoren
// (Anton) kann man immer noch den Button bezahlt klicken." Die Datei war erzeugt, die Maske
// zeigte weiter „festgeschrieben" und bot „Bezahlt" an — weil die Liste den Exportvermerk gar
// nicht mitlieferte.
//
// ⚠️ Eine erzeugte Datei ist KEIN geflossenes Geld: Sie muss noch bei der Bank hochgeladen
// werden, und die Bank kann sie abweisen. Deshalb steht dazwischen ein eigener Zustand, und
// deshalb bestaetigt der Betreiber erst, wenn die Bank ausgefuehrt hat.
const ZAHLUNG: Record<NonNullable<StatementRow['paymentState']>, string> = T.payment

/** Was in der Zustandsspalte steht. Der Zahlungsweg gewinnt, wo es einen gibt — bei einer
 *  ausgestellten Gutschrift ist „zur Zahlung eingereicht" die Auskunft, auf die es ankommt,
 *  nicht „festgeschrieben". */
function zustandText(r: StatementRow): string {
    return r.paymentState ? ZAHLUNG[r.paymentState] : STATUS[r.status]
}

export default function AuthorStatements() {
    const { openTab } = useTabs()
    // ⚠️ Diese Liste zeigt AUTORENdaten — Name und „fehlt: IBAN, USt-IdNr." — und in den
    // aufgeklappten Verkäufen die Artikeltitel aus dem Katalog. Sie war die einzige Ansicht im
    // Haus, die `useDataVersion` gar nicht benutzte: Wer eine USt-IdNr. nachtrug, sah hier
    // weiter „fehlt: USt-IdNr." (Betreiber, 10.09.2026: „Da fehlt noch unser interner Action
    // Hook").
    //
    // ⚠️ Der Satz, an dem man die übrigen Fälle findet, ohne sie zu suchen: Listen hören auf
    // ihre EIGENE Ressource, nicht auf die, deren Daten sie mit anzeigen. `AuthorsList` und
    // `CustomersList` machen es richtig; `AuthorCatalog` (Autorennamen) und `InvoicesList`
    // (Kundennamen) haben dieselbe Lücke — als Befund gemeldet, bewusst nicht mitgezogen.
    const fremdVersion = useDataVersion('authors') + useDataVersion('catalog')
    const { toast } = useToast()
    const { can } = useAuth()
    const canWrite = can('invoices', 'write')

    const [periods, setPeriods] = useState<string[]>([])
    const [period, setPeriod] = useState('')
    const [brand, setBrand] = useState(AUTHOR_BRAND)
    const [rows, setRows] = useState<StatementRow[]>([])
    const [busy, setBusy] = useState('')

    // ── Reiter „Verkäufe" ────────────────────────────────────────────────────────────────
    // Die Liste hinter der Nachabrechnung. Sie hängt zwingend an EINEM Autor — ohne ihn wäre
    // es die Liste aller Verkäufe aller Autoren, und darin findet niemand die drei Zeilen, um
    // die es geht. Ein Zeitraumfilter ist bewusst NICHT vorgewählt: Wer eine zurückgehaltene Zeile
    // aus dem Vormonat zurückholen will, verlöre sie sonst genau dadurch.
    const [view, setView] = useState<'statements' | 'sales'>('statements')
    const [authors, setAuthors] = useState<AuthorRow[]>([])
    const [authorId, setAuthorId] = useState('')
    const [sales, setSales] = useState<AuthorSaleRow[]>([])
    const [picked, setPicked] = useState<Set<string>>(new Set())
    const [salesBusy, setSalesBusy] = useState(false)

    // ⚠️ Die Liste haengt an Periode UND MARKE. Bis zum 09.09.2026 lud sie alle Marken gemischt,
    // waehrend der Knopf daneben immer nur EINE abrechnete — die Markenauswahl steuerte also nur
    // den Knopf, nicht das, was darunter stand. Solange nur eine Marke Autoren hat, faellt das
    // nicht auf; mit zweien saehe er eine Liste, die zu seinem Knopf nicht passt, und „3 stehen
    // an" waere eine andere Zahl als die, die er vor sich hat. Gefunden beim Umbau, nicht
    // gemeldet bekommen.
    const load = useCallback(async (p: string, b: string) => {
        if (!p || !b) return
        try { setRows((await listStatements({ period: p, brand: b })).rows) }
        catch (e: any) { toast(e?.response?.data?.error ?? e?.message ?? T.loadFailed, 'err') }
    }, [toast])

    const loadSales = useCallback(async (aid: string) => {
        if (!aid) { setSales([]); return }
        setSalesBusy(true)
        try { setSales((await listAuthorSales({ authorId: aid })).rows); setPicked(new Set()) }
        catch (e: any) { toast(e?.response?.data?.error ?? T.salesLoadFailed, 'err') }
        finally { setSalesBusy(false) }
    }, [toast])

    useEffect(() => {
        if (view !== 'sales' || authors.length) return
        // Alle Autoren fürs Auswahlfeld — die Liste blättert ab 50, deshalb ausdrücklich mehr.
        listAuthors({ pageSize: LOOKUP_FETCH_LIMIT }).then((r) => setAuthors(r.rows)).catch(() => setAuthors([]))
    }, [view, authors.length])

    useEffect(() => { void loadSales(authorId) }, [authorId, loadSales])

    useEffect(() => {
        void (async () => {
            try {
                const { periods: ps, suggestion } = await statementPeriods()
                setPeriods(ps)
                setPeriod((cur) => cur || suggestion)
            } catch (e: any) { toast(e?.message ?? T.periodsLoadFailed, 'err') }
        })()
    }, [toast])

    // ⚠️ `fremdVersion` gehört in die Abhängigkeiten, nicht nur in den Aufruf oben: Ein Zähler,
    // der steigt, ohne dass ein Effekt daran hängt, bewirkt gar nichts — und niemand merkt es.
    useEffect(() => { void load(period, brand) }, [period, brand, load, fremdVersion])

    // The row awaiting confirmation for "Bezahlt". Held as the whole row, because the dialog
    // names the author and the amount — a confirmation that only asks "sure?" adds a click
    // without adding a check.
    // The credit note opens in an app tab, like every other document in the back office. It
    // was the last one still opening a browser tab — this page was built six weeks AFTER the
    // tab pattern and simply did not pick it up; the endpoint serves `inline`, so there was
    // never anything in the way. (The month export's ZIP stays a browser download: res.download
    // sends it as an attachment, and an archive has nothing to display in a viewer.)
    // Tab title and saved name are the file name the server puts at the end of the address (58a).
    const openPdf = (r: StatementRow) => openTab({
        id: `statement-pdf-${r._id}`,
        name: r.pdfName,
        component: 'file-viewer',
        config: { url: statementPdfUrl(r._id, r.pdfName), fname: r.pdfName },
    })

    const [confirmPaid, setConfirmPaid] = useState<StatementRow | null>(null)
    // ⛔ Zwei getrennte Rueckwege, siehe die Knoepfe in der Zeile.
    const [confirmUnpay, setConfirmUnpay] = useState<StatementRow | null>(null)
    const [confirmReturned, setConfirmReturned] = useState<StatementRow | null>(null)

    const ladeRuns = useCallback(async (p: string, b: string) => {
        if (!p) { setRuns([]); return }
        try { setRuns(await sepaRuns(b, p)) } catch { setRuns([]) }
    }, [])
    useEffect(() => { void ladeRuns(period, brand) }, [period, brand, ladeRuns])

    async function act(id: string, what: string, fn: () => Promise<unknown>, done: string) {
        setBusy(`${id}:${what}`)
        try {
            await fn()
            await load(period, brand)
            await ladeRuns(period, brand)
            toast(done)
        } catch (e: any) { toast(e?.response?.data?.error ?? e?.message ?? T.failed, 'err') }
        finally { setBusy('') }
    }

    async function run() {
        const [y, m] = period.split('-').map(Number)
        setBusy('run')
        try {
            const res = await runStatements(brand, y, m)
            await load(period, brand)
            // ⚠️ Die Meldung nennt BEIDES. Seit „Festschreiben" aus der Maske verschwunden ist,
            // sieht niemand mehr rot in der Liste stehen, was liegen blieb — eine Rückmeldung
            // „12 abgerechnet", hinter der drei unvollständige Autoren stecken, wäre genau die
            // stille Lücke, die dieser Umbau schließen soll.
            const teile: string[] = []
            if (res.finalized) teile.push(fill(plural(res.finalized, T.runFinalized), { n: res.finalized }))
            if (res.pending.length) {
                teile.push(fill(T.runPending, { n: res.pending.length })
                    + res.pending.map((p) => `${p.authorName} (${p.missing.join(', ')})`).join('; '))
            }
            if (res.failed.length) teile.push(fill(T.runFailed, { n: res.failed.length }))
            if (!teile.length) teile.push(T.runNothing)
            toast(`${periodLabel(period)}: ${teile.join(' — ')}`,
                res.pending.length || res.failed.length ? 'warn' : 'ok')
        } catch (e: any) { toast(e?.response?.data?.error ?? e?.message ?? T.runError, 'err') }
        finally { setBusy('') }
    }

    // What the month costs us in total — the number you want before you start transferring.
    // ⚠️ `pending` wird MITGEZAEHLT, aber getrennt ausgewiesen. Beides zusammenzuwerfen waere
    // eine Zahl, die Bestand und Aussicht mischt; nur den Bestand zu zeigen hiesse, den Monat
    // zu klein darzustellen, solange nicht abgerechnet wurde.
    // ⚠️ Die Fusszeile zeigt, WO das Geld steht — drei Stufen statt einer Zahl:
    //   noch nicht abgerechnet → im Entwurf → zur Ueberweisung.
    // Vorher hiess es pauschal „X noch zu ueberweisen", und X war alles ausser dem Bezahlten.
    // Das war schon fuer Entwuerfe falsch (die muessen erst festgeschrieben werden) und wurde
    // mit den anstehenden Zeilen grob falsch: Es versprach eine Ueberweisung fuer Geld, das
    // noch gar nicht abgerechnet ist. Beim Ansehen des ersten Bildes aufgefallen — die Zahl
    // stand da und war um die Anwaerter zu hoch.
    const sum = useMemo(() => rows.reduce((t, r) => ({
        payout: t.payout + (r.totals?.payout ?? 0),
        toPay: t.toPay + (r.totals?.toPay ?? 0),
        // Nur festgeschrieben: erst dann gibt es einen Beleg, auf den man ueberweisen kann.
        // ⚠️ UND nur, was noch in KEINER Datei war. Bis zum 10.09.2026 stand hier
        // `status === 'finalized'`, und damit zaehlte eine bereits exportierte Gutschrift weiter
        // als „zur Ueberweisung": Der Fuss meldete 246,90 €, der Knopf bot eine Datei an — und
        // das Erzeugen haette „Nichts zu ueberweisen" geantwortet, weil buildSepaFile
        // Exportiertes ueberspringt. Eine Zahl, die zu nichts passt, was der Klick danach tut.
        open: t.open + (r.paymentState === 'open' ? (r.totals?.toPay ?? 0) : 0),
        // Eingereicht, aber noch nicht bestaetigt — eigener Topf, weil es weder „offen" noch
        // „erledigt" ist. Es ist das Geld, auf das der Betreiber im Kontoauszug wartet.
        submitted: t.submitted + (r.paymentState === 'submitted' ? (r.totals?.toPay ?? 0) : 0),
        // Zwei Toepfe statt einem: Ein Entwurf, dem Angaben fehlen, wird vom naechsten Lauf
        // NICHT festgeschrieben — ihn mit den uebrigen zusammenzuzaehlen hiesse, Geld
        // anzukuendigen, das ohne Zutun nicht kommt.
        draftReady: t.draftReady + (r.status === 'draft' && !r.missing.length ? (r.totals?.toPay ?? 0) : 0),
        draftBlocked: t.draftBlocked + (r.status === 'draft' && r.missing.length ? (r.totals?.toPay ?? 0) : 0),
        pendingCount: t.pendingCount + (r.status === 'pending' ? 1 : 0),
        pendingPayout: t.pendingPayout + (r.status === 'pending' ? (r.totals?.payout ?? 0) : 0),
        draftCount: t.draftCount + (r.status === 'draft' ? 1 : 0),
    }), { payout: 0, toPay: 0, open: 0, submitted: 0, draftReady: 0, draftBlocked: 0, pendingCount: 0, pendingPayout: 0, draftCount: 0 }), [rows])

    // Wie viele AUTOREN der Lauf anfasst — nicht wie viele Zeilen.
    // ⚠️ Seit ein Autor zweimal in der Liste stehen kann (Entwurf + anstehende Verkäufe), ist
    // das nicht mehr dasselbe. Der Lauf erzeugt EINEN Beleg je Autor, also ist der Autor die
    // richtige Einheit; „Monat abrechnen (3)" bei zwei Zeilen desselben Autors wäre eine Zahl,
    // die zu nichts passt, was danach entsteht.
    // Entwürfe zählen mit: Seit der Lauf auch festschreibt, ist ein Entwurf keine erledigte
    // Zeile, sondern eine offene — entweder er wird jetzt festgeschrieben, oder es fehlt etwas.
    const zuTun = useMemo(() => new Set(
        rows.filter((r) => r.status === 'pending' || r.status === 'draft').map((r) => r.authorId),
    ).size, [rows])

    // Was der Lauf erzeugt: die Vergütung ALLER Zeilen, die er anfasst — Entwürfe UND anstehende.
    // ⚠️ Nicht nur das eine oder das andere: Bei einem Autor mit beidem entsteht EINE Abrechnung
    // über die Summe. Dass die Zahl auch in der Fußzeile steht, ist keine Verdopplung — dort
    // beantwortet sie „was kostet der Monat", hier „was passiert, wenn ich drücke".
    const laufSumme = useMemo(() => rows
        .filter((r) => r.status === 'pending' || r.status === 'draft')
        .reduce((t, r) => t + (r.totals?.payout ?? 0), 0), [rows])

    // Autoren, bei denen Entwurf UND anstehende Zeilen zusammenkommen — sie erklären, was der
    // Klick tut. Nur die Anzahl, kein Namensteppich: Bei fünf solchen Autoren wäre eine
    // Aufzählung unlesbar, und ein einzelner Name stünde stellvertretend für etwas, das mehrere
    // betrifft.
    const zusammenfuehrung = useMemo(
        () => rows.filter((r) => r.status === 'pending' && r.partOfDraft), [rows])

    // Wer nach dem Lauf als Entwurf liegen bleibt — je Autor einmal, auch wenn er zwei Zeilen hat.
    const bleibtLiegen = useMemo(() => {
        const m = new Map<string, { name: string, missing: string[] }>()
        for (const r of rows) {
            if ((r.status === 'draft' || r.status === 'pending') && r.missing.length) {
                m.set(r.authorId, { name: r.authorName, missing: r.missing })
            }
        }
        return [...m.values()]
    }, [rows])

    // ⚠️ Die Nachfrage vor dem Lauf. Sie fehlte bis zum 09.09.2026, und der Kommentar an dieser
    // Stelle begründete das ausdrücklich: „Monat abrechnen" sei umkehrbar, eine Bestätigung, die
    // nichts sichert, gewöhne man sich nur ab wegzuklicken. Das war richtig, solange der Knopf
    // nur Entwürfe baute — seit er festschreibt und Belegnummern zieht, ist die Begründung durch
    // den eigenen Umbau ungültig geworden. „Ein fehlerhafter Klick würde sofort auslösen."
    //
    // Damit sie nicht die fünfte Erklärung dieser Seite wird: Die Zeile über der Liste nennt den
    // PLAN, dieser Dialog die FOLGE. Nicht dieselbe Auskunft zweimal.
    const [confirmRun, setConfirmRun] = useState(false)

    // ── SEPA-Sammelauftrag ────────────────────────────────────────────────────
    // ⚠️ Erst sehen, dann auslösen — hier noch mehr als beim Abrechnen: Die Datei bewegt Geld.
    // Die Vorschau ändert nichts und bekommt die IBANs nur maskiert.
    const [sepa, setSepa] = useState<{ lines: SepaLine[], total: number, skipped: SepaSkipped[] } | null>(null)
    const [sepaOffen, setSepaOffen] = useState(false)
    // Die Auszahlungslaeufe der Periode — eine erzeugte SEPA-Datei je Eintrag.
    const [runs, setRuns] = useState<SepaRun[]>([])
    const [confirmRun2, setConfirmRun2] = useState<SepaRun | null>(null)

    async function sepaAnsehen() {
        setBusy('sepa')
        try {
            setSepa(await sepaPreview(brand, period))
            setSepaOffen(true)
        } catch (e: any) {
            toast(e?.response?.data?.error ?? e?.message ?? T.previewFailed, 'err')
        } finally { setBusy('') }
    }

    // Eine geaenderte IBAN bestaetigen — danach die Vorschau neu, damit die Gutschrift in die
    // Datei rueckt (oder mit neuem Grund stehen bleibt).
    async function ibanBestaetigen(statementId: string, iban: string) {
        setBusy('sepa')
        try {
            await confirmStatementIban(statementId, iban)
            setSepa(await sepaPreview(brand, period))
            toast(T.ibanConfirmed)
        } catch (e: any) {
            toast(e?.response?.data?.error ?? e?.message ?? T.confirmFailed, 'err')
        } finally { setBusy('') }
    }

    async function sepaErzeugen() {
        setBusy('sepa')
        try {
            const r = await sepaExport(brand, period)
            // ⚠️ Der Download geschieht im Browser, die Datei wird NICHT auf dem Server abgelegt.
            // Sie enthält die Bankverbindungen aller ausgezahlten Autoren im Klartext — genau
            // das, wogegen die Verschlüsselung in der Datenbank gebaut ist.
            const url = URL.createObjectURL(new Blob([r.xml], { type: 'application/xml' }))
            const a = document.createElement('a')
            a.href = url
            a.download = `sepa-${brand}-${period}-${r.msgId}.xml`
            a.click()
            URL.revokeObjectURL(url)
            setSepaOffen(false)
            setSepa(null)
            await load(period, brand)
            // Der neue Lauf muss sofort in der Dateiliste stehen — dort wird er später bestätigt.
            await ladeRuns(period, brand)
            toast(fill(plural(r.count, T.sepaDone), { count: r.count, total: eur(r.total), msgId: r.msgId }))
        } catch (e: any) {
            toast(e?.response?.data?.error ?? e?.message ?? T.exportFailed, 'err')
        } finally { setBusy('') }
    }

    // Was der Lauf WIRKLICH festschreibt — je Autor einmal, und nur die vollständigen.
    // ⚠️ Nicht dasselbe wie `zuTun`/`laufSumme`: Die zählen alles, was angefasst wird. Wer
    // Angaben schuldet, wird zwar neu gebaut, aber nicht festgeschrieben.
    const wirdFest = useMemo(() => {
        const m = new Map<string, number>()
        for (const r of rows) {
            if ((r.status === 'draft' || r.status === 'pending') && !r.missing.length) {
                m.set(r.authorId, (m.get(r.authorId) ?? 0) + (r.totals?.payout ?? 0))
            }
        }
        return { count: m.size, payout: [...m.values()].reduce((t, v) => t + v, 0) }
    }, [rows])

    // Aufgeklappte Zeilen und ihre nachgeladenen Verkaeufe. Der Schluessel ist der AUTOR, nicht
    // die Abrechnung — eine anstehende Zeile hat keine Id.
    // ⚠️ Der Schluessel ist die ZEILE, nicht der Autor. Seit ein Autor mit Entwurf UND
    // anstehenden Verkaeufen zweimal in der Liste steht, wuerden beide gleichzeitig aufklappen —
    // und die eine zeigte die Verkaeufe der anderen.
    const rowKey = (r: StatementRow) => r._id || `${r.authorId}:pending`

    // Der erste Tag des gewählten Monats — daran erkennt die Detailzeile, dass ein Verkauf aus
    // einem früheren Monat nachgeholt wird.
    const monatsAnfang = useMemo(() => {
        const [y, m] = (period || '').split('-').map(Number)
        return y && m ? new Date(y, m - 1, 1) : new Date(0)
    }, [period])
    const [openRows, setOpenRows] = useState<Record<string, StatementLine[] | 'loading'>>({})
    // Beim Wechsel von Monat oder Marke schliessen — eine offene Detailzeile zeigte sonst die
    // Verkaeufe der vorigen Auswahl weiter, unter der neuen Ueberschrift.
    useEffect(() => { setOpenRows({}) }, [period, brand])
    async function toggleRow(r: StatementRow) {
        const key = rowKey(r)
        if (openRows[key]) { setOpenRows((o) => { const n = { ...o }; delete n[key]; return n }) ; return }
        setOpenRows((o) => ({ ...o, [key]: 'loading' }))
        try {
            const d = await statementLines(r.brand, r.period, r.authorId)
            // ⚠️ Jede Zeile zeigt NUR ihre eigenen Verkäufe. Steht ein Autor zweimal in der
            // Liste, muss der Entwurf zeigen, was IN IHM ist, und die anstehende Zeile, was
            // dazukommt — sonst stünde derselbe Verkauf zweimal untereinander, und der Leser
            // fragt sich, ob er zweimal gezahlt wird. Dieselbe Antwort, zwei Ausschnitte; eine
            // zweite Route dafür wäre eine zweite Stelle, an der es auseinanderlaufen kann.
            const teil = r.partOfDraft
                ? d.lines.filter((l) => !l.inDraft)     // die anstehende Zeile: nur das Neue
                : r.status === 'draft'
                    ? d.lines.filter((l) => l.inDraft)  // der Entwurf: nur, was in ihm steht
                    : d.lines
            setOpenRows((o) => ({ ...o, [key]: teil }))
        } catch {
            setOpenRows((o) => { const n = { ...o }; delete n[key]; return n })
            toast(T.linesLoadFailed, 'err')
        }
    }

    return (
        <div className="page">
            <div className="page-title">
                <h1>{T.title}</h1>
                <InfoTip>
                    {T.info.part1}{' '}
                    {/* ⚠️ Bis zum 10.09.2026 stand hier nur „Bezahlt hält fest, dass sie raus ist"
                        — und damit fehlte genau die Stufe dazwischen, an der der Betreiber
                        hängenblieb: die erzeugte Datei, die noch bei niemandem war. */}
                    {T.info.part2}<b>{T.info.bold}</b>{T.info.part3}
                </InfoTip>
            </div>

            <div className="pill-tabs" role="tablist">
                <button type="button" role="tab" className={`pill-tab${view === 'statements' ? ' active' : ''}`}
                    onClick={() => setView('statements')}>{T.tabStatements}</button>
                <button type="button" role="tab" className={`pill-tab${view === 'sales' ? ' active' : ''}`}
                    onClick={() => setView('sales')}>{T.tabSales}</button>
            </div>

            {view === 'sales' && <SalesTab
                authors={authors} authorId={authorId} setAuthorId={setAuthorId}
                sales={sales} busy={salesBusy} picked={picked} setPicked={setPicked}
                canWrite={canWrite}
                onRecharge={async () => {
                    setSalesBusy(true)
                    try {
                        const r = await rechargeAuthorSales([...picked])
                        // ⚠️ „vorgemerkt" allein beschreibt einen Zustand, der sich von selbst
                        // einzustellen scheint. Er tut es nicht: Der Monatslauf ist Handarbeit.
                        // Der Betreiber hat am 09.09.2026 genau deshalb in der Abrechnungsliste
                        // nachgesehen und die Zeile nicht gefunden — er hatte getan, was der Text
                        // ihm nahelegte.
                        toast(fill(T.rechargeDone, { updated: r.updated, payout: eur(r.payout), period: periodLabel(r.period) })
                            + (r.skipped ? fill(T.rechargeSkipped, { n: r.skipped }) : '')
                            + fill(T.rechargeVisible, { period: periodLabel(r.period) }))
                        await loadSales(authorId)
                    } catch (e: any) {
                        toast(e?.response?.data?.error ?? T.rechargeFailed, 'err')
                    } finally { setSalesBusy(false) }
                }}
            />}

            {view === 'statements' && <>
            <div className="row" style={{ gap: 12, alignItems: 'flex-end', marginBottom: 16 }}>
                <div className="field" style={{ maxWidth: 200 }}>
                    <label>{T.period}</label>
                    <select value={period} onChange={(e) => setPeriod(e.target.value)}>
                        {periods.map((p) => <option key={p} value={p}>{periodLabel(p)}</option>)}
                    </select>
                </div>
                <div className="field" style={{ maxWidth: 180 }}>
                    <label>{T.brand}</label>
                    <select value={brand} onChange={(e) => setBrand(e.target.value)}>
                        {BRANDS.map((b) => <option key={b.key} value={b.key}>{b.name}</option>)}
                    </select>
                </div>
                {/* ⚠️ KEINE feste Breite. Mit `maxWidth: 280` brach die Beschriftung um, sobald
                    der Betrag dazukam — „Jetzt ist er zweizeilig" (Betreiber, 09.09.2026). Eine
                    größere feste Zahl wäre dieselbe Falle im nächsten Monat: Die Autorenzahl ist
                    ein- oder zweistellig, der Betrag hat mal vier, mal neun Zeichen („9,90 €"
                    gegen „12.345,67 €"). Der Knopf wächst deshalb mit seinem Inhalt und bricht
                    nicht um (`.btn-wide`). */}
                <div className="field btn-wide">
                    <label>&nbsp;</label>
                    {/* ⚠️ Die Zahl steht AUF dem Knopf, nicht daneben. Der Betreiber hat ihn nicht
                        gedrückt, weil er nicht wusste, was dabei herauskommt — also gehört die
                        Auskunft dorthin, wo die Hand ist. Die Anzahl, nicht die Summe: die steht
                        in der Liste und in der Fußzeile, zweimal wäre Verdopplung.
                        Und der Knopf sagt, dass er auch VORHANDENE Entwürfe neu baut — das hätte
                        sonst niemand erwartet. */}
                    <div className="row" style={{ gap: 6, alignItems: 'center' }}>
                        <button type="button" onClick={() => setConfirmRun(true)}
                            disabled={!canWrite || !period || busy === 'run' || zuTun === 0}
                            title={zuTun === 0
                                ? T.runNothingTitle
                                : T.runTitle}>
                            {busy === 'run' ? T.running
                                : zuTun === 0 ? T.nothingToSettle
                                : fill(plural(zuTun, T.runButton), { authors: zuTun, sum: eur(laufSumme) })}
                        </button>
                        {/* ⚠️ Der Hinweis sitzt AM KNOPF, der es tut. „Neu aufbauen" und
                            „Festschreiben" sind verschwunden — ein Klick erledigt jetzt beides,
                            ohne Zwischenschritt. Damit wird der Satz zur Unveränderlichkeit
                            WICHTIGER, nicht überflüssig. */}
                        <InfoTip>
                            {T.runTip.part1}<b>{T.runTip.bold1}</b>{T.runTip.part2}
                            <b>{T.runTip.bold2}</b>{T.runTip.part3}<br /><br />
                            {T.runTip.part4}<b>{T.runTip.bold3}</b>{T.runTip.part5}<b>{T.runTip.bold4}</b>{T.runTip.part6}<br /><br />
                            {T.runTip.part7}
                        </InfoTip>
                    </div>
                </div>

                {/* Der zweite Schritt: Was festgeschrieben ist, wird überwiesen. Eigener Knopf,
                    weil es eine eigene Entscheidung ist — und er zeigt erst die Vorschau. */}
                <div className="field btn-wide">
                    <label>&nbsp;</label>
                    <button type="button" className="secondary"
                        disabled={!canWrite || !period || busy === 'sepa' || sum.open <= 0}
                        title={sum.open <= 0
                            ? T.sepaNothingTitle
                            : T.sepaTitle}
                        onClick={() => void sepaAnsehen()}>
                        {/* ⚠️ KEIN Betrag auf diesem Knopf, anders als bei „Monat abrechnen".
                            Dort löst der Klick aus, hier öffnet er nur die Vorschau — und die
                            Liste kann den Betrag gar nicht genau kennen: Wer keine IBAN hat oder
                            außerhalb des SEPA-Raums sitzt, fällt erst beim Erzeugen heraus. Ein
                            erster Versuch zeigte `sum.open` und damit 361,45 €, während in der
                            Vorschau 298,45 € standen; die Differenz war ein ausgeschlossener
                            Autor. Ein Betrag, der gleich ein anderer ist, ist schlechter als
                            keiner. Die Auslassungspunkte sagen: Es kommt noch ein Schritt. */}
                        {busy === 'sepa' ? '…' : sum.open > 0 ? T.sepaCreateEllipsis : T.noTransfer}
                    </button>
                </div>
            </div>

            {/* ⛔ DIE AUSZAHLUNGSLÄUFE — und der eine Knopf, um den es geht.
                Betreiber, 10.09.2026: „wenn ich bei 20 Autoren jeden Monat 20x den Bezahlt Button
                klicken muss, werde ich verrückt. Das muss einfacher gehen." Eine Datei, ein
                Upload, eine Bestätigung — die Bestätigung gehört an den LAUF, nicht an jede
                einzelne Gutschrift.

                ⚠️ Und deshalb steht hier eine eigene Liste: Eine erzeugte Datei ist ein Vorgang
                für sich. Sie liegt zwischen „festgeschrieben" und „bezahlt", und ohne diese Liste
                gäbe es keinen Ort, an dem man sie sieht — die Abrechnungsliste zeigt Autoren,
                nicht Dateien. */}
            {runs.length > 0 && (
                <div className="card" style={{ marginTop: 16 }}>
                    <h2 style={{ marginTop: 0 }}>
                        {T.runsTitle}
                        <InfoTip>
                            {T.runsTip.part1}<b>{T.runsTip.bold}</b>{T.runsTip.part2}
                        </InfoTip>
                    </h2>
                    <table className="table">
                        <thead>
                            <tr>
                                <th>{T.colCreated}</th>
                                <th>{T.colFile}</th>
                                <th className="num">{T.colCreditNotes}</th>
                                <th className="num">{T.colSum}</th>
                                <th>{T.colState}</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {runs.map((lauf) => {
                                const offen = lauf.count - lauf.paidCount
                                return (
                                    <tr key={lauf.sepaExportId}>
                                        <td>{new Date(lauf.exportedAt).toLocaleDateString('de-DE')}</td>
                                        {/* Die MsgId steht so auch in der Datei und im Kontoauszug —
                                            damit lässt sich eine Rückfrage der Bank zuordnen. */}
                                        <td><code style={{ fontSize: '0.85em' }}>{lauf.sepaExportId}</code></td>
                                        <td className="num">{lauf.count}</td>
                                        <td className="num">{eur(lauf.total)}</td>
                                        <td>
                                            {offen === 0
                                                ? T.payment.paid
                                                : lauf.paidCount === 0
                                                    ? T.payment.submitted
                                                    /* Teilweise bestätigt: jemand hat einzelne
                                                       Gutschriften separat behandelt. Das ist
                                                       erlaubt, muss aber sichtbar sein. */
                                                    : fill(T.partlyPaid, { paid: lauf.paidCount, count: lauf.count })}
                                        </td>
                                        <td>
                                            {offen > 0
                                                ? (
                                                    <button type="button"
                                                        disabled={!canWrite || !!busy}
                                                        onClick={() => setConfirmRun2(lauf)}>
                                                        {offen === lauf.count
                                                            ? T.markAllPaid
                                                            : fill(T.markRestPaid, { n: offen })}
                                                    </button>
                                                )
                                                : <span className="hint">{T.done}</span>}
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* ⚠️ Dieser Hinweis stand frueher NUR bei leerer Liste — und verschwand damit genau
                dann, wenn jemand eine WEITERE Abrechnung erwartet: Wer eine Zeile nachverguetet
                hat, sieht eine gefuellte Liste ohne seinen Autor darin und schliesst auf einen
                Fehler. Genau so geschehen am 09.09.2026. Der Lauf ist Handarbeit — er steht in
                keinem Zeitplan, auch nicht in der Produktion —, also muss das dauerhaft dastehen
                und nicht nur beim ersten Mal. */}
            {/* Die Liste zeigt seit dem 09.09.2026 auch, was NOCH NICHT abgerechnet ist. Der alte
                Hinweis „Monat abrechnen erstellt die Entwürfe" stand nur bei LEERER Liste und
                verschwand damit genau dann, wenn jemand eine weitere Abrechnung erwartete. Jetzt
                beantwortet die Liste selbst, was ansteht — der Text sagt nur noch, was der Zustand
                „steht an" bedeutet. */}
            {rows.length === 0 ? (
                <div className="notice">
                    {fill(T.noSales, { period: period ? periodLabel(period) : T.thisMonth })}
                </div>
            ) : zuTun > 0 && (
                // ⚠️ EINE Zeile, die den konkreten Fall beschreibt — sie ERSETZT den früheren
                // allgemeinen Hinweis, sie steht nicht daneben. Die Seite erklärte an vier
                // Stellen, und der Betreiber fragte trotzdem, was die Zustände aussagen sollen;
                // ein fünfter Text war nachweislich nicht das Mittel. Diese hier nennt seine
                // Zahlen und beantwortet die Frage dort, wo sie entsteht (09.09.2026).
                <div className="hint" style={{ marginBottom: 8 }}>
                    {T.plan.before}{fill(plural(zuTun, T.plan.count), { n: zuTun })}
                    {' '}{T.overTotal}<b>{eur(laufSumme)}</b>.
                    {/* Nur, wenn es den Fall wirklich gibt — sonst zeigte der Satz ins Leere. */}
                    {zusammenfuehrung.length > 0 && (
                        <>{T.plan.mergeAt}{zusammenfuehrung.length === 1
                            ? <><b>{zusammenfuehrung[0].authorName}</b>{T.plan.mergeOne}</>
                            : <>{fill(T.plan.mergeMany, { n: zusammenfuehrung.length })}</>}
                        {' '}{T.plan.mergeText}<b>{T.plan.mergeBold}</b>{T.plan.mergeEnd}</>
                    )}
                    {' '}{T.plan.tail}
                </div>
            )}

            {rows.length > 0 && (
                <>
                    <table>
                        <thead>
                            <tr>
                                <th style={{ width: 28 }}></th>
                                <th>{T.colAuthor}</th>
                                {/* ⚠️ „Verkäufe", nicht „Positionen". Der Betreiber hat den alten Titel
                                    fuer eine laufende ZEILENNUMMER gehalten und wollte sie weg — die
                                    Spalte selbst brauchte er. „Position" ist Beleg-Sprache; in einer
                                    Liste von Verkaeufen verspricht sie etwas anderes, als sie zeigt
                                    (09.09.2026). */}
                                <th className="num">{T.colSales}</th>
                                <th className="num">{T.colPayoutNet}</th>
                                <th className="num">{T.colPayout}</th>
                                <th>{T.colStatus}</th>
                                <th>{T.colDocument}</th>
                                {/* ⚠️ Hier stand ein Hilfetext zu „Neu aufbauen" und „Festschreiben"
                                    — zwei Schaltflächen, die es seit dem Umbau nicht mehr gibt. Er
                                    ist beim Entfernen der Knöpfe stehen geblieben und erklärte
                                    danach Handlungen, die niemand mehr ausführen kann. Genau das
                                    Muster wie beim verworfenen Wort „ausgebucht", das in der Maske
                                    ersetzt war und in zwei Meldungen weiterlebte. Was noch gilt,
                                    steht jetzt am Knopf „Monat abrechnen", der es tut. */}
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (<Fragment key={rowKey(r) + r.period}>
                                {/* ⚠️ Anstehende Zeilen sind gedämpft, und das ist kein Schmuck: Sie
                                    zeigen etwas, das es NOCH NICHT GIBT. Der Unterschied ist groesser
                                    als der zwischen Entwurf und festgeschrieben, also darf er nicht
                                    nur im Statuswort stecken — wer ueber die Liste sieht, muss ohne
                                    Lesen erkennen, was Bestand ist und was Aussicht. */}
                                <tr className={[
                                    r.status === 'pending' ? 'row-pending' : '',
                                    // Eingerückt: Sie gehört zur Zeile darüber. Ohne das las der
                                    // Betreiber zwei Einträge desselben Autors als zwei Vorgänge.
                                    r.partOfDraft ? 'row-attached' : '',
                                ].filter(Boolean).join(' ') || undefined}>
                                    <td>
                                        {/* Ein Zeichen, das sich dreht — wie `.panel-chevron` es im Haus
                                            schon macht. Zwei verschiedene Zeichen zu tauschen waere ein
                                            zweites Muster fuer dieselbe Geste. */}
                                        <button type="button" className="row-toggle" title={T.showSales}
                                            aria-expanded={!!openRows[rowKey(r)]}
                                            aria-label={openRows[rowKey(r)] ? T.hideSales : T.showSales}
                                            onClick={() => void toggleRow(r)}>
                                            <span aria-hidden>▾</span>
                                        </button>
                                    </td>
                                    <td>
                                        {/* Der Zustand der USt-IdNr. neben dem Namen — dasselbe Zeichen
                                            wie in der Autorenmaske und in der Autorenliste, aus
                                            `vatMark` und nicht noch einmal hergeleitet. An dieser
                                            Zeile haengt Geld: Ob die Nummer bestaetigt ist, entscheidet
                                            ueber den Reverse Charge, und bisher musste man dafuer die
                                            Maske des Autors oeffnen. (Betreiber, 11.09.2026: „Icon mit
                                            Tooltip fuer den Status als Text") */}
                                        <div>{r.authorName}{r.vat && <> <VatMark stand={r.vat} /></>}</div>
                                        {/* Derselbe Autor steht zweimal da — einmal mit dem, was im
                                            Dokument ist, einmal mit dem, was dazukommt. Ohne diesen
                                            Zusatz sähe es aus wie zwei Personen gleichen Namens. */}
                                        {r.partOfDraft && (
                                            <div className="hint">{T.addsToDraft}</div>
                                        )}
                                        {/* Was dem AUTOR fehlt, steht an seiner Entwurfszeile — an
                                            der zweiten wäre es dieselbe Auskunft ein zweites Mal. */}
                                        {r.missing.length > 0 && !r.partOfDraft && (
                                            <span className="hint" style={{ color: 'var(--danger)' }}>
                                                {T.missing}{r.missing.join(', ')}
                                            </span>
                                        )}
                                        {r.freeAccessCount > 0 && r.missing.length === 0 && (
                                            <span className="hint">{fill(plural(r.freeAccessCount, T.freeAccess), { n: r.freeAccessCount })}</span>
                                        )}
                                    </td>
                                    <td className="num">{r.lineCount}</td>
                                    <td className="num">{eur(r.totals?.payout ?? 0)}</td>
                                    <td className="num">
                                        {/* Bei einer Zeile, die zu einem bestehenden Entwurf
                                            gehört, gibt es keine eigene Auszahlung: Was ausgezahlt
                                            wird, entscheidet sich am ganzen Dokument (Schwelle,
                                            Vortrag). „0,00 €" behauptete, er bekomme nichts. */}
                                        {r.partOfDraft ? <span className="hint">—</span> : eur(r.totals?.toPay ?? 0)}
                                        {!!r.totals?.carriedOut && (
                                            <div className="hint">
                                                {r.totals.carriedOut > 0
                                                    ? fill(T.carriedOut, { amount: eur(r.totals.carriedOut) })
                                                    : fill(T.owedToUs, { amount: eur(-r.totals.carriedOut) })}
                                            </div>
                                        )}
                                    </td>
                                    <td>
                                        {zustandText(r)}
                                        {r.paidAt && <div className="hint">{new Date(r.paidAt).toLocaleDateString('de-DE')}</div>}
                                        {/* Bei „eingereicht" zaehlt, WANN die Datei entstand —
                                            danach fragt der Betreiber, wenn er beim Kontoauszug
                                            sucht. */}
                                        {r.paymentState === 'submitted' && r.sepaExportedAt && (
                                            <div className="hint">
                                                {fill(T.fileFrom, { date: new Date(r.sepaExportedAt).toLocaleDateString('de-DE') })}
                                            </div>
                                        )}
                                    </td>
                                    <td>
                                        {/* Ein Anwaerter hat kein Dokument — leer heisst hier „gibt es
                                            noch nicht", und das ist die richtige Aussage. */}
                                        {r.status === 'pending' ? <span className="hint">—</span> : (
                                            <button type="button" className="link-btn" onClick={() => openPdf(r)}>
                                                {r.documentNumber ?? T.viewDraft}
                                            </button>
                                        )}
                                    </td>
                                    <td style={{ whiteSpace: 'nowrap' }}>
                                        {/* ⚠️ „Neu aufbauen" und „Festschreiben" sind am 09.09.2026
                                            verschwunden — beides tut jetzt „Monat abrechnen"
                                            (Betreiber: „intern pro Position aufgerufen"). Ein
                                            Entwurf, der hier steht, wartet deshalb IMMER auf etwas
                                            Fehlendes; die Zeile sagt worauf, statt einen Knopf
                                            anzubieten, der ohnehin abgelehnt würde. */}
                                        {r.status === 'draft' && (
                                            <span className="hint">
                                                {r.missing.length ? T.waitingForData : T.finalizedNextRun}
                                            </span>
                                        )}
                                        {/* ⚠️ „Als bezahlt markieren", nicht „Bezahlt" (Betreiber,
                                            10.09.2026: „der Button Bezahlt ist irrefuehrend. Man
                                            nimmt an, dass bereits bezahlt wurde."). Eine
                                            Handlung, kein Zustand — und er sagt, was wirklich
                                            passiert: eine Markierung, keine Zahlung.
                                            ⚠️ Bei einem ganzen Lauf klickt hier NIEMAND je Zeile;
                                            dafuer gibt es den Knopf am Lauf weiter unten. Dieser
                                            hier ist fuer den Autor, der separat ueberwiesen
                                            wurde. */}
                                        {(r.paymentState === 'open' || r.paymentState === 'submitted') && (
                                            <button type="button" disabled={!canWrite || !!busy}
                                                onClick={() => setConfirmPaid(r)}>
                                                {T.markPaid}
                                            </button>
                                        )}
                                        {/* ⛔ ZWEI Rueckwege, und sie duerfen nicht zusammenfallen.
                                            „Markierung zuruecknehmen" laesst den Exportvermerk
                                            stehen — die Gutschrift kommt NICHT erneut in eine
                                            Datei, sonst bekaeme der Autor sein Geld zweimal.
                                            „Ueberweisung kam zurueck" nimmt ihn mit, damit sie in
                                            die naechste Datei kommt. Deshalb zwei Knoepfe und
                                            nicht einer mit Rueckfrage: Bei einer Geldfrage ist ein
                                            weggeklickter Dialog zu wenig Widerstand. */}
                                        {/* ⚠️ Eigene Zeile, nicht daneben. Beim Ansehen im Browser
                                            lief „Überweisung kam zurück" rechts aus der Tabelle
                                            heraus — die Spalte steht auf `nowrap`, und zwei
                                            Knöpfe nebeneinander passen dort nicht. Im
                                            Seitentext war davon nichts zu merken; erst das
                                            Bild hat es gezeigt. */}
                                        {(r.paymentState === 'submitted' || r.paymentState === 'paid') && (
                                            <div style={{ marginTop: 4, display: 'flex', gap: 10 }}>
                                                {r.paymentState === 'paid' && (
                                                    <button type="button" className="link-btn" disabled={!canWrite || !!busy}
                                                        onClick={() => setConfirmUnpay(r)}>
                                                        {T.undoMark}
                                                    </button>
                                                )}
                                                <button type="button" className="link-btn" disabled={!canWrite || !!busy}
                                                    onClick={() => setConfirmReturned(r)}>
                                                    {T.transferReturned}
                                                </button>
                                            </div>
                                        )}
                                        {/* Ein Anwaerter hat nichts, was man an IHM tun koennte —
                                            der einzige Weg dorthin ist „Monat abrechnen". */}
                                        {r.status === 'pending' && <span className="hint">—</span>}
                                    </td>
                                </tr>
                                {openRows[rowKey(r)] && (
                                    <tr className="row-detail">
                                        <td></td>
                                        <td colSpan={6}>
                                            {openRows[rowKey(r)] === 'loading' ? <span className="hint">{T.loading}</span> : (
                                                <table className="inner-table">
                                                    {/* Feste Spalten — sonst richtet sich jede
                                                        aufgeklappte Zeile nach ihrem eigenen
                                                        Inhalt aus, und zwei offene Eintraege
                                                        fluchten nicht mehr. */}
                                                    <colgroup>
                                                        <col className="c-datum" />
                                                        <col className="c-artikel" />
                                                        <col className="c-art" />
                                                        <col className="c-zahl" />
                                                        <col className="c-zahl" />
                                                        <col className="c-zahl" />
                                                    </colgroup>
                                                    <thead>
                                                        <tr>
                                                            <th>{T.colSold}</th><th>{T.colItem}</th><th>{T.colKind}</th>
                                                            <th className="num">{T.colListPrice}</th>
                                                            <th className="num">{T.colFee}</th>
                                                            <th className="num">{T.colPayoutLine}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {/* ⚠️ Hier stehen ALLE Zeilen, die zu diesem Monat
                                                            gehören — auch die, die noch in keinem Dokument
                                                            sind. „Ich werde keine Monat abrechnen drücken um
                                                            weitere Einträge zu sehen" (Betreiber, 09.09.2026).
                                                            Zwei Träger für den Unterschied, nicht einer: das
                                                            Wort „kommt dazu" UND die abgesetzte Zeile. */}
                                                        {(openRows[rowKey(r)] as StatementLine[]).map((l) => (
                                                            <tr key={l.saleId} className={l.inDraft ? undefined : 'line-new'}>
                                                                <td>{new Date(l.soldAt).toLocaleDateString('de-DE')}</td>
                                                                <td>
                                                                    {l.title}
                                                                    {!l.inDraft && (
                                                                        <div className="hint" style={{ color: 'var(--warn)' }}>
                                                                            {T.addedNextRun}
                                                                        </div>
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    {l.isChargebackFee ? T.chargebackFee : l.isReversal ? (l.reversalReason === 'chargeback' ? SALE_KIND_LABELS.chargeback : SALE_KIND_LABELS.reversal) : SALE_KIND_LABELS.sale}
                                                                    {/* Eine Zeile aus einem anderen Monat muss sagen, WARUM sie hier steht. */}
                                                                    {l.isCatchUp && <div className="hint">{T.catchUp}</div>}
                                                                    {/* Dasselbe für den zweiten Weg, auf dem eine alte Zeile hierher
                                                                        kommt: Ihr eigener Monat ist festgeschrieben, also wird sie
                                                                        hier nachgeholt. Ohne den Vermerk ist ein Datum, das nicht
                                                                        zum Monat passt, ein Rätsel. */}
                                                                    {!l.isCatchUp && new Date(l.soldAt) < monatsAnfang && (
                                                                        <div className="hint">{T.earlierMonth}</div>
                                                                    )}
                                                                </td>
                                                                <td className="num">{eur(l.listPrice)}</td>
                                                                {/* Die Pauschale ist keine Vermittlungsgebühr — Spalte leer, Betrag unter Vergütung. */}
                                                                <td className="num">{l.isChargebackFee ? '—' : eur(l.fee)}</td>
                                                                <td className="num">{eur(l.payout)}</td>
                                                            </tr>
                                                        ))}
                                                        {!(openRows[rowKey(r)] as StatementLine[]).length && (
                                                            <tr><td colSpan={6} className="hint">{T.noLines}</td></tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            )}
                                        </td>
                                    </tr>
                                )}
                            </Fragment>))}
                        </tbody>
                        <tfoot>
                            <tr>
                                <th colSpan={3}>{fill(T.sumLabel, { period: periodLabel(period) })}</th>
                                <th className="num">{eur(sum.payout)}</th>
                                <th className="num">{eur(sum.toPay)}</th>
                                <th colSpan={3} className="hint">
                                    {sum.pendingPayout > 0 && <div>{fill(T.notYetSettled, { amount: eur(sum.pendingPayout) })}</div>}
                                    {/* ⚠️ Hier stand „erst festschreiben" — eine Anweisung auf einen
                                        Knopf, den es nicht mehr gibt: „Das ist verwirrend, weil
                                        keiner weiß was das bedeutet" (Betreiber, 09.09.2026).
                                        Aber „erst Monat abrechnen" wäre nur für den VOLLSTÄNDIGEN
                                        Entwurf richtig. Wem Angaben fehlen, bei dem ändert ein
                                        weiterer Klick gar nichts — der Satz schickte ihn zum
                                        zweiten Mal an einen Knopf, der nichts tut. */}
                                    {sum.draftReady > 0 && (
                                        <div>{fill(T.draftReady, { amount: eur(sum.draftReady) })}</div>
                                    )}
                                    {sum.draftBlocked > 0 && (
                                        <div>{fill(T.draftBlocked, { amount: eur(sum.draftBlocked) })}</div>
                                    )}
                                    {/* ⚠️ Zwei Zahlen, nicht eine: „zur Überweisung" ist, was noch
                                        in keine Datei gekommen ist; „eingereicht" ist, was in
                                        einer liegt und auf die Bank wartet. Zusammengezählt wären
                                        sie eine Zahl, nach der niemand handeln kann. */}
                                    <div>{sum.open > 0 ? fill(T.toTransfer, { amount: eur(sum.open) }) : T.nothingToTransfer}</div>
                                    {sum.submitted > 0 && (
                                        <div className="hint">{fill(T.submittedSum, { amount: eur(sum.submitted) })}</div>
                                    )}
                                </th>
                            </tr>
                        </tfoot>
                    </table>
                </>
            )}

            </>}

            {/* "Bezahlt" cannot be undone: there is no route back to `finalized`, and buildStatement
                deliberately refuses to touch anything past draft. Since the author portal shows
                `paid` as "am … ausbezahlt", a mis-click tells them about a transfer that never
                happened — and they rely on it. Hence the amount and the name in the question:
                confirming is only a check if there is something to check against. */}
            {/* ⚠️ Die Vorschau der SEPA-Datei. Sie muss dieselbe Qualität haben wie die
                Abrechnungsliste — hier sogar mehr, weil es Geld bewegt und nicht einen Entwurf.
                Und der AUSSCHLUSS steht vor dem Einschluss: Ein übersehener Autor meldet sich;
                ein Autor, der fälschlich drin ist, bekommt Geld, und niemand merkt es. */}
            <ConfirmDialog
                open={sepaOffen}
                title={T.sepaCreate}
                confirmLabel={sepa?.lines.length
                    ? fill(plural(sepa.lines.length, T.sepaConfirm), { n: sepa.lines.length })
                    : T.download}
                busy={busy === 'sepa'}
                message={sepa && (
                    <>
                        {sepa.skipped.length > 0 && (
                            <span style={{ display: 'block', marginBottom: 10 }}>
                                <b style={{ color: 'var(--danger)' }}>
                                    {fill(plural(sepa.skipped.length, T.skippedHead), { n: sepa.skipped.length })}
                                </b>
                                <span style={{ display: 'block' }}>
                                    {sepa.skipped.filter((k) => !k.ibanCheck).map((k) => `${k.authorName} (${k.reason})`).join('; ')}
                                </span>
                                {/* ⛔ Eine geaenderte IBAN wird nie still ueberwiesen: Sie steht hier VOLL, damit
                                    man sie vor dem Bestaetigen mit dem Autor abgleichen kann. */}
                                {sepa.skipped.filter((k) => k.ibanCheck).map((k) => (
                                    <span key={k.documentNumber} style={{ display: 'block', marginTop: 6 }}>
                                        {k.authorName} · {k.documentNumber}: {k.reason}{T.newIban}<code>{k.ibanCheck!.iban}</code>{' '}
                                        {can('invoices', 'write') && (
                                            <button type="button" className="btn btn-sm" disabled={busy === 'sepa'}
                                                onClick={() => ibanBestaetigen(k.ibanCheck!.statementId, k.ibanCheck!.iban)}>
                                                {T.confirmIban}
                                            </button>
                                        )}
                                    </span>
                                ))}
                            </span>
                        )}
                        {sepa.lines.length === 0 ? (
                            <span style={{ display: 'block' }}>
                                <b>{T.nothingLeft}</b>
                            </span>
                        ) : (
                            <>
                                <span style={{ display: 'block' }}>
                                    {fill(plural(sepa.lines.length, T.transfersCount), { n: sepa.lines.length })}
                                    {' '}{T.overTotal}<b>{eur(sepa.total)}</b>:
                                </span>
                                <span style={{ display: 'block', marginTop: 6 }}>
                                    {sepa.lines.map((l) => (
                                        <span key={l.statementId} style={{ display: 'block' }}>
                                            {l.authorName} · {l.documentNumber} · {l.iban} · <b>{eur(l.amount)}</b>
                                        </span>
                                    ))}
                                </span>
                                <span style={{ display: 'block', marginTop: 10 }}>
                                    {T.sepaNote.part1}<b>{T.sepaNote.bold}</b>{T.sepaNote.part2}
                                </span>
                            </>
                        )}
                    </>
                )}
                onConfirm={() => { if (sepa?.lines.length) void sepaErzeugen(); else setSepaOffen(false) }}
                onCancel={() => { if (busy !== 'sepa') { setSepaOffen(false); setSepa(null) } }}
            />

            <ConfirmDialog
                open={confirmRun}
                title={T.runConfirmTitle}
                confirmLabel={T.runConfirmLabel}
                busy={busy === 'run'}
                message={
                    <>
                        {/* ⚠️ Die FOLGE, nicht der Plan — der steht über der Liste. Und sie muss
                            zwischen dem unterscheiden, was WIRKLICH festgeschrieben wird, und dem,
                            was liegen bleibt: Ein erster Entwurf sagte „2 Autoren, 8.888,87 € —
                            daraus werden Gutschriften", während beiden Autoren Angaben fehlten und
                            gar keine entstanden wäre. Erst das Bild hat das gezeigt. */}
                        {wirdFest.count > 0 ? (
                            <span style={{ display: 'block' }}>
                                {fill(plural(wirdFest.count, T.fix.count), { n: wirdFest.count })}
                                {' '}{T.fix.together}<b>{eur(wirdFest.payout)}</b>{T.fix.sie}
                                {' '}{plural(wirdFest.count, T.fix.get)}{T.fix.number}
                                {' '}{plural(wirdFest.count, T.fix.is)}{T.fix.after}<b>{T.fix.bold}</b>.
                            </span>
                        ) : (
                            <span style={{ display: 'block' }}>
                                <b>{T.nothingFixedBold}</b>{T.nothingFixed}
                            </span>
                        )}
                        {bleibtLiegen.length > 0 && (
                            <span style={{ display: 'block', marginTop: 8 }}>
                                {T.leftBefore}{plural(bleibtLiegen.length, T.leftVerb)}{T.leftAfter}{' '}
                                {bleibtLiegen.map((b) => fill(T.leftEntry, { name: b.name, missing: b.missing.join(', ') })).join('; ')}.
                            </span>
                        )}
                    </>
                }
                onConfirm={() => { setConfirmRun(false); void run() }}
                onCancel={() => { if (busy !== 'run') setConfirmRun(false) }}
            />

            <ConfirmDialog
                open={!!confirmPaid}
                title={T.paidConfirm}
                confirmLabel={T.paidConfirm}
                busy={busy.endsWith(':paid')}
                message={confirmPaid && (
                    <>
                        <span style={{ display: 'block' }}>
                            <b>{confirmPaid.authorName}</b> — {confirmPaid.documentNumber ?? T.settlementFallback}{T.over}{' '}
                            <b>{eur(confirmPaid.totals?.toPay ?? 0)}</b>
                        </span>
                        <span style={{ display: 'block', marginTop: 8 }}>
                            {T.paidNote1.part1}<b>{T.paidNote1.bold}</b>{T.paidNote1.part2}
                        </span>
                        {/* ⚠️ Hier stand bis zum 10.09.2026 „das lässt sich nicht rückgängig
                            machen" — seit es die beiden Rückwege gibt, stimmt das nicht mehr.
                            Ein Satz, der eine Endgültigkeit behauptet, die es nicht gibt, macht
                            den Betreiber unnötig vorsichtig und ist beim nächsten Fehlklick eine
                            Lüge. */}
                        <span style={{ display: 'block', marginTop: 8 }}>
                            {T.paidNote2}<b>{T.undoMark}</b>.
                        </span>
                    </>
                )}
                onConfirm={() => {
                    const row = confirmPaid
                    if (!row) return
                    setConfirmPaid(null)
                    void act(row._id, 'paid', () => markStatementPaid(row._id), T.paidDone)
                }}
                onCancel={() => { if (!busy) setConfirmPaid(null) }}
            />

            {/* ⛔ DIE ZWEI RUECKWEGE. Beide enden bei „nicht bezahlt" — aber nur einer laesst die
                Gutschrift erneut in eine Ueberweisungsdatei. Deshalb sagt jeder Dialog genau das,
                worin sie sich unterscheiden: ob wieder ueberwiesen wird oder nicht. */}
            {/* ⚠️ Auch der Sammelknopf fragt nach — er bewegt mehr Geld auf einmal als jeder
                andere in dieser Maske. Die Frage nennt die Zahl und die Summe, damit das
                Bestätigen eine Prüfung ist und kein Reflex. */}
            <ConfirmDialog
                open={!!confirmRun2}
                title={T.run2Title}
                confirmLabel={T.markPaid}
                busy={busy.endsWith(':run-paid')}
                message={confirmRun2 && (() => {
                    const offen = confirmRun2.count - confirmRun2.paidCount
                    return (
                        <>
                            <span style={{ display: 'block' }}>
                                {T.run2File}<code>{confirmRun2.sepaExportId}</code>{T.run2From}{' '}
                                {new Date(confirmRun2.exportedAt).toLocaleDateString('de-DE')}
                            </span>
                            <span style={{ display: 'block', marginTop: 8 }}>
                                <b>{fill(plural(offen, T.run2Count), { n: offen })}</b>
                                {' '}{T.run2Marked}<b>{eur(confirmRun2.total)}</b>.
                            </span>
                            <span style={{ display: 'block', marginTop: 8 }}>
                                {T.run2Note.part1}<b>{T.run2Note.bold}</b>{' '}
                                {T.run2Note.part2}
                            </span>
                        </>
                    )
                })()}
                onConfirm={() => {
                    const lauf = confirmRun2
                    if (!lauf) return
                    setConfirmRun2(null)
                    void act(lauf.sepaExportId, 'run-paid',
                        () => markSepaRunPaid(lauf.sepaExportId), T.run2Done)
                }}
                onCancel={() => { if (!busy) setConfirmRun2(null) }}
            />

            <ConfirmDialog
                open={!!confirmUnpay}
                title={T.undoMark}
                confirmLabel={T.undoMark}
                busy={busy.endsWith(':unpay')}
                message={confirmUnpay && (
                    <>
                        <span style={{ display: 'block' }}>
                            <b>{confirmUnpay.authorName}</b> — {confirmUnpay.documentNumber ?? T.settlementFallback}{T.over}{' '}
                            <b>{eur(confirmUnpay.totals?.toPay ?? 0)}</b>
                        </span>
                        <span style={{ display: 'block', marginTop: 8 }}>
                            {T.unpayNote1.part1}<b>{T.unpayNote1.bold}</b>{T.unpayNote1.part2}
                        </span>
                        <span style={{ display: 'block', marginTop: 8 }}>
                            {T.unpayNote2.part1}<b>{T.unpayNote2.bold}</b>{T.unpayNote2.part2}<b>{T.transferReturned}</b>.
                        </span>
                    </>
                )}
                onConfirm={() => {
                    const row = confirmUnpay
                    if (!row) return
                    setConfirmUnpay(null)
                    void act(row._id, 'unpay', () => unmarkStatementPaid(row._id), T.unpayDone)
                }}
                onCancel={() => { if (!busy) setConfirmUnpay(null) }}
            />

            <ConfirmDialog
                open={!!confirmReturned}
                title={T.transferReturned}
                confirmLabel={T.payAgain}
                busy={busy.endsWith(':returned')}
                message={confirmReturned && (
                    <>
                        <span style={{ display: 'block' }}>
                            <b>{confirmReturned.authorName}</b> — {confirmReturned.documentNumber ?? T.settlementFallback}{T.over}{' '}
                            <b>{eur(confirmReturned.totals?.toPay ?? 0)}</b>
                        </span>
                        <span style={{ display: 'block', marginTop: 8 }}>
                            {T.returnedNote1.part1}<b>{T.returnedNote1.bold}</b>{T.returnedNote1.part2}
                        </span>
                        <span style={{ display: 'block', marginTop: 8 }}>
                            {T.returnedNote2.part1}<b>{T.returnedNote2.bold}</b>{T.returnedNote2.part2}
                            {' '}{T.returnedNote2.part3}<b>{T.returnedNote2.bold2}</b>{T.returnedNote2.part4}
                        </span>
                    </>
                )}
                onConfirm={() => {
                    const row = confirmReturned
                    if (!row) return
                    setConfirmReturned(null)
                    void act(row._id, 'returned', () => statementPaymentReturned(row._id),
                        T.returnedDone)
                }}
                onCancel={() => { if (!busy) setConfirmReturned(null) }}
            />

        </div>
    )
}


// Der Reiter „Verkäufe": alle Verkäufe EINES Autors, mit dem, was aus jedem geworden ist.
//
// ⚠️ Zwei Spalten, nicht eine. Die Merkmale schließen sich nicht aus — eine Stornozeile kann
// längst abgerechnet sein, und was heute vorgemerkt ist, ist nächsten Monat abgerechnet. Eine
// einzige Spalte müsste einen Vorrang erfinden und dabei den Verlierer verschweigen.
//
// ⚠️ Ankreuzen geht NUR bei zurückgehaltenen Zeilen. Eine Auswahlbox an einer abgerechneten Zeile
// wäre ein Versprechen, das die Aktion nicht hält: Was in einer festgeschriebenen Abrechnung
// sitzt, wird nicht mehr angefasst.
function SalesTab({ authors, authorId, setAuthorId, sales, busy, picked, setPicked, canWrite, onRecharge }: {
    authors: AuthorRow[]
    authorId: string
    setAuthorId: (id: string) => void
    sales: AuthorSaleRow[]
    busy: boolean
    picked: Set<string>
    setPicked: (s: Set<string>) => void
    canWrite: boolean
    onRecharge: () => void
}) {
    const holbar = sales.filter((r) => r.state === 'excluded')
    const summe = sales.filter((r) => picked.has(r._id)).reduce((t, r) => t + r.payout, 0)
    const toggle = (id: string) => {
        const next = new Set(picked)
        if (next.has(id)) next.delete(id); else next.add(id)
        setPicked(next)
    }

    return (
        <>
            <div className="row" style={{ gap: 12, alignItems: 'flex-end', marginBottom: 16 }}>
                <div className="field" style={{ maxWidth: 320 }}>
                    <label>{T.colAuthor}</label>
                    <select value={authorId} onChange={(e) => setAuthorId(e.target.value)}>
                        <option value="">{T.pickPlaceholder}</option>
                        {authors.map((a) => (
                            <option key={a._id} value={a._id}>{a.displayName}{fill(T.wpRef, { ref: a.externalRef })}</option>
                        ))}
                    </select>
                </div>
                <div className="field" style={{ maxWidth: 320 }}>
                    <label>&nbsp;</label>
                    <button type="button" disabled={!canWrite || busy || picked.size === 0} onClick={onRecharge}>
                        {busy ? '…' : `${T.recharge}${picked.size ? ` (${picked.size}, ${eur(summe)})` : ''}`}
                    </button>
                </div>
            </div>

            {!authorId && <p className="muted">{T.pickAuthor}</p>}

            {authorId && sales.length === 0 && !busy && (
                <p className="muted">{T.noSalesForAuthor}</p>
            )}

            {authorId && sales.length > 0 && (
                <div className="panel">
                    {holbar.length === 0 && (
                        <p className="muted" style={{ marginTop: 0 }}>
                            {T.nothingToRecharge}{SALE_STATE_LABELS.excluded}.
                        </p>
                    )}
                    <table>
                        <thead>
                            <tr>
                                <th style={{ width: 34 }}></th>
                                <th>{T.colSold}</th>
                                <th>{T.colItem}</th>
                                <th>{T.colKind}</th>
                                <th>{T.colState}</th>
                                <th className="num">{T.colListPrice}</th>
                                <th className="num">{T.colPayoutLine}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sales.map((r) => {
                                const holbarZeile = r.state === 'excluded'
                                return (
                                    <tr key={r._id}>
                                        <td>
                                            {holbarZeile
                                                ? <input type="checkbox" checked={picked.has(r._id)}
                                                    disabled={!canWrite} onChange={() => toggle(r._id)}
                                                    aria-label={fill(T.selectLine, { title: r.title })} />
                                                : null}
                                        </td>
                                        <td style={{ whiteSpace: 'nowrap' }}>{new Date(r.soldAt).toLocaleDateString('de-DE')}</td>
                                        <td>{r.title} <span className="hint">#{r.itemId}</span></td>
                                        <td>{SALE_KIND_LABELS[r.kind]}</td>
                                        <td>
                                            {SALE_STATE_LABELS[r.state]}
                                            {r.state === 'pending' && r.catchUpPeriod && (
                                                <span className="hint">{fill(T.catchUpFor, { period: periodLabel(r.catchUpPeriod) })}</span>
                                            )}
                                        </td>
                                        <td className="num">{eur(r.listPrice)}</td>
                                        <td className="num">{eur(r.payout)}</td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    )
}
